/**
 * Financial Reports Service
 * Generates IAS-compliant financial statements and reports
 */

import { supabase } from '@/lib/supabase'
import type { FinancialReport, FinancialRatio } from './financeService'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { formatCurrency } from '@/lib/currency'
import { format } from 'date-fns'

// =====================================================
// FINANCIAL REPORTS SERVICE
// =====================================================

/**
 * Format currency for PDF export (jsPDF-safe)
 * Uses GHS code instead of symbol to avoid Unicode rendering issues
 */
function formatCurrencyForPDF(amount: number): string {
  return `GHS ${amount.toLocaleString('en-GH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * Add church logo to PDF header
 * @param doc jsPDF document instance
 * @param logoUrl URL to church logo
 * @param x X position for logo
 * @param y Y position for logo
 * @param width Logo width
 * @param height Logo height
 */
async function addLogoToPDF(
  doc: jsPDF,
  logoUrl: string | null | undefined,
  x: number = 14,
  y: number = 10,
  width: number = 20,
  height: number = 20
): Promise<void> {
  if (!logoUrl) return

  try {
    // Fetch the image and convert to base64
    const response = await fetch(logoUrl)
    const blob = await response.blob()

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        try {
          const base64data = reader.result as string
          // Determine image format
          const format = logoUrl.toLowerCase().endsWith('.png') ? 'PNG' : 'JPEG'
          doc.addImage(base64data, format, x, y, width, height)
          resolve()
        } catch (error) {
          console.error('Error adding logo to PDF:', error)
          resolve() // Continue without logo on error
        }
      }
      reader.onerror = () => {
        console.error('Error reading logo file')
        resolve() // Continue without logo on error
      }
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error fetching logo:', error)
    // Continue without logo on error
  }
}

/**
 * Get church data including logo
 */
async function getChurchData(churchId: string): Promise<{ name: string; logoUrl: string | null }> {
  try {
    const { data, error } = await supabase
      .from('churches')
      .select('name, display_name, logo_url')
      .eq('id', churchId)
      .single()

    if (error) throw error

    return {
      name: data?.display_name || data?.name || 'Church',
      logoUrl: data?.logo_url || null
    }
  } catch (error) {
    console.error('Error fetching church data:', error)
    return { name: 'Church', logoUrl: null }
  }
}

export class ReportService {
  /**
   * Get Statement of Financial Position (Balance Sheet) - FASB ASC 958 (Non-Profit)
   * Format: Assets = Liabilities + Net Assets
   */
  static async getStatementOfFinancialPosition(
    churchId: string,
    asOfDate: string,
    fundId?: string
  ): Promise<import('@/services/financeService').StatementOfFinancialPosition> {
    // Get all accounts and their current balances
    const { data: accounts, error } = await supabase
      .from('chart_of_accounts')
      .select('id, account_code, account_name, account_type, current_balance')
      .eq('church_id', churchId)
      .eq('is_active', true)
      .order('account_code')

    if (error) throw error

    const accountsList = accounts || []

    // Group accounts by type
    const assets = accountsList
      .filter(a => a.account_type === 'asset')
      .map(a => ({
        account_id: a.id,
        account_code: a.account_code,
        account_name: a.account_name,
        balance: a.current_balance || 0
      }))

    const liabilities = accountsList
      .filter(a => a.account_type === 'liability')
      .map(a => ({
        account_id: a.id,
        account_code: a.account_code,
        account_name: a.account_name,
        balance: a.current_balance || 0
      }))

    // Calculate totals
    const total_assets = assets.reduce((sum, a) => sum + a.balance, 0)
    const total_liabilities = liabilities.reduce((sum, a) => sum + a.balance, 0)

    // For non-profits: Net Assets = Assets - Liabilities
    // This includes the equity accounts PLUS accumulated surplus (retained earnings)
    // Get revenue and expense totals to calculate current year surplus
    const revenue_accounts = accountsList.filter(a => a.account_type === 'revenue')
    const expense_accounts = accountsList.filter(a => a.account_type === 'expense')

    const total_revenue = revenue_accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0)
    const total_expenses = expense_accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0)
    const current_year_surplus = total_revenue - total_expenses

    // Get equity/net asset accounts
    const equity_accounts = accountsList.filter(a => a.account_type === 'equity')
    const equity_balance = equity_accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0)

    // Total Net Assets = Equity Accounts + Current Year Surplus
    // This makes the balance sheet balance!
    const total_net_assets = equity_balance + current_year_surplus

    // Build net assets display: show equity accounts + current year change
    const net_assets = [
      ...equity_accounts.map(a => ({
        account_id: a.id,
        account_code: a.account_code,
        account_name: a.account_name,
        balance: a.current_balance || 0
      })),
      // Add current year surplus as a line item
      {
        account_id: 'current-year-surplus',
        account_code: '3900',
        account_name: 'Current Year Surplus/(Deficit)',
        balance: current_year_surplus
      }
    ]

    return {
      assets,
      liabilities,
      net_assets,
      total_assets,
      total_liabilities,
      total_net_assets
    }
  }

  /**
   * Get Statement of Activities (Income Statement / P&L) - FASB ASC 958
   */
  static async getStatementOfActivities(
    churchId: string,
    startDate: string,
    endDate: string,
    fundId?: string
  ): Promise<import('@/services/financeService').StatementOfActivities> {
    // For Statement of Activities, we show year-to-date revenue and expenses
    // These are stored in the current_balance of revenue/expense accounts

    // Get all revenue and expense accounts with their current balances
    const { data: accounts, error } = await supabase
      .from('chart_of_accounts')
      .select('id, account_code, account_name, account_type, current_balance')
      .eq('church_id', churchId)
      .eq('is_active', true)
      .in('account_type', ['revenue', 'expense'])
      .order('account_code')

    if (error) throw error

    const accountsList = accounts || []

    // Filter and map revenue accounts
    const revenue = accountsList
      .filter(a => a.account_type === 'revenue')
      .filter(a => (a.current_balance || 0) !== 0) // Only show accounts with activity
      .map(a => ({
        account_id: a.id,
        account_code: a.account_code,
        account_name: a.account_name,
        amount: a.current_balance || 0
      }))

    // Filter and map expense accounts
    const expenses = accountsList
      .filter(a => a.account_type === 'expense')
      .filter(a => (a.current_balance || 0) !== 0) // Only show accounts with activity
      .map(a => ({
        account_id: a.id,
        account_code: a.account_code,
        account_name: a.account_name,
        amount: a.current_balance || 0
      }))

    // Calculate totals
    const total_revenue = revenue.reduce((sum, r) => sum + r.amount, 0)
    const total_expenses = expenses.reduce((sum, e) => sum + e.amount, 0)
    const net_income = total_revenue - total_expenses

    return {
      revenue,
      expenses,
      total_revenue,
      total_expenses,
      net_income
    }
  }

  /**
   * Get Statement of Cash Flows - IAS 7
   */
  static async getCashFlowStatement(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<import('@/services/financeService').CashFlowStatement> {
    // Simplified cash flow statement
    // TODO: Implement proper cash flow categorization

    // Get contributions (operating inflow)
    const { data: contributions } = await supabase
      .from('contributions')
      .select('amount')
      .eq('church_id', churchId)
      .eq('status', 'completed')
      .gte('contribution_date', startDate)
      .lte('contribution_date', endDate)

    const contributionsTotal = contributions?.reduce((sum, c) => sum + c.amount, 0) || 0

    // Get expenses (operating outflow)
    const { data: expenses } = await supabase
      .from('expenses')
      .select('total_amount')
      .eq('church_id', churchId)
      .eq('status', 'paid')
      .gte('expense_date', startDate)
      .lte('expense_date', endDate)

    const expensesTotal = expenses?.reduce((sum, e) => sum + e.total_amount, 0) || 0

    // Calculate cash flows
    const operating_net = contributionsTotal - expensesTotal

    // Get cash account balances
    const { data: cashAccounts } = await supabase
      .from('chart_of_accounts')
      .select('current_balance')
      .eq('church_id', churchId)
      .eq('account_type', 'asset')
      .ilike('account_name', '%cash%')

    const currentCash = cashAccounts?.reduce((sum, a) => sum + (a.current_balance || 0), 0) || 0

    return {
      operating_activities: {
        contributions: contributionsTotal,
        expenses: expensesTotal,
        net: operating_net
      },
      investing_activities: {
        asset_purchases: 0,
        asset_sales: 0,
        net: 0
      },
      financing_activities: {
        borrowings: 0,
        repayments: 0,
        net: 0
      },
      net_change: operating_net,
      beginning_balance: currentCash - operating_net,
      ending_balance: currentCash
    }
  }

  /**
   * Get Statement of Changes in Net Assets (Fund Balance Changes)
   */
  static async getStatementOfChangesInNetAssets(
    churchId: string,
    fiscalYear: number
  ): Promise<any[]> {
    const { data, error } = await supabase.rpc('get_statement_of_changes_in_net_assets', {
      p_church_id: churchId,
      p_fiscal_year: fiscalYear
    })

    if (error) throw error
    return data || []
  }

  /**
   * Get Budget vs Actual Variance Report
   */
  static async getBudgetVarianceReport(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<import('@/services/financeService').BudgetVarianceReport> {
    // Simplified - returns empty for now
    // TODO: Implement budget variance calculation when budgets are created
    return {
      line_items: [],
      total_budgeted: 0,
      total_actual: 0,
      total_variance: 0
    }
  }

  /**
   * Get Budget vs Actual Report
   */
  static async getBudgetVsActualReport(
    churchId: string,
    budgetId: string
  ): Promise<any[]> {
    const { data, error } = await supabase
      .from('v_budget_vs_actual')
      .select('*')
      .eq('church_id', churchId)
      .eq('budget_id', budgetId)
      .order('account_code')

    if (error) throw error
    return data || []
  }

  /**
   * Get Budget vs Actual by Period (Monthly)
   */
  static async getBudgetVsActualByPeriod(
    churchId: string,
    budgetId: string,
    fiscalYear: number
  ): Promise<{
    accounts: any[]
    periods: Record<number, {
      budget: number
      actual: number
      variance: number
      variance_percentage: number
    }>
  }> {
    // Get budget lines with period breakdown
    const { data: budgetLines, error: budgetError } = await supabase
      .from('budget_lines')
      .select('*, account:chart_of_accounts(*)')
      .eq('budget_id', budgetId)

    if (budgetError) throw budgetError

    // Get actual amounts by period
    const { data: actuals, error: actualsError } = await supabase
      .from('general_ledger_lines')
      .select('account_id, journal_entry:general_ledger(fiscal_period), debit_amount, credit_amount')
      .eq('church_id', churchId)
      .eq('journal_entry.fiscal_year', fiscalYear)
      .eq('journal_entry.status', 'posted')

    if (actualsError) throw actualsError

    // Calculate actuals by account and period
    const actualsByAccountPeriod: Record<string, Record<number, number>> = {}

    actuals?.forEach((line: any) => {
      const accountId = line.account_id
      const period = line.journal_entry.fiscal_period
      const amount = line.debit_amount - line.credit_amount

      if (!actualsByAccountPeriod[accountId]) {
        actualsByAccountPeriod[accountId] = {}
      }
      if (!actualsByAccountPeriod[accountId][period]) {
        actualsByAccountPeriod[accountId][period] = 0
      }
      actualsByAccountPeriod[accountId][period] += amount
    })

    // Build response with variance analysis
    const accounts = budgetLines?.map((line: any) => {
      const periods: Record<number, any> = {}

      for (let period = 1; period <= 12; period++) {
        const budgetAmount = (line as any)[`period_${period}_budget`] || 0
        const actualAmount = actualsByAccountPeriod[line.account_id]?.[period] || 0
        const variance = actualAmount - budgetAmount
        const variancePercentage = budgetAmount !== 0 ? (variance / budgetAmount) * 100 : 0

        periods[period] = {
          budget: budgetAmount,
          actual: actualAmount,
          variance,
          variance_percentage: variancePercentage
        }
      }

      return {
        account: line.account,
        periods
      }
    }) || []

    return {
      accounts,
      periods: {} // Overall period totals could be calculated here
    }
  }

  /**
   * Get Financial Ratios
   */
  static async getFinancialRatios(
    churchId: string,
    asOfDate: string
  ): Promise<FinancialRatio[]> {
    const { data, error } = await supabase.rpc('get_financial_ratios', {
      p_church_id: churchId,
      p_as_of_date: asOfDate
    })

    if (error) throw error
    return data || []
  }

  /**
   * Get Giving Analytics Report
   */
  static async getGivingAnalytics(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<{
    summary: {
      total_givers: number
      total_amount: number
      average_gift: number
      total_transactions: number
    }
    by_type: Record<string, { amount: number; count: number; percentage: number }>
    by_method: Record<string, { amount: number; count: number; percentage: number }>
    by_fund: Record<string, { amount: number; count: number; percentage: number }>
    trends: {
      date: string
      amount: number
      count: number
    }[]
    top_givers: {
      member_id: string
      member_name: string
      total_amount: number
      transaction_count: number
    }[]
  }> {
    const { data, error } = await supabase
      .from('contributions')
      .select(`
        *,
        member:members(
          id,
          profile:profiles(id, first_name, last_name)
        ),
        fund:funds(fund_name)
      `)
      .eq('church_id', churchId)
      .eq('status', 'completed')
      .gte('contribution_date', startDate)
      .lte('contribution_date', endDate)

    if (error) throw error

    const contributions = data || []
    const totalAmount = contributions.reduce((sum, c) => sum + c.amount, 0)
    const uniqueGivers = new Set(contributions.filter(c => c.member_id).map(c => c.member_id)).size

    // By type
    const byType: Record<string, { amount: number; count: number; percentage: number }> = {}
    contributions.forEach(c => {
      if (!byType[c.contribution_type]) {
        byType[c.contribution_type] = { amount: 0, count: 0, percentage: 0 }
      }
      byType[c.contribution_type].amount += c.amount
      byType[c.contribution_type].count += 1
    })
    Object.keys(byType).forEach(type => {
      byType[type].percentage = (byType[type].amount / totalAmount) * 100
    })

    // By method
    const byMethod: Record<string, { amount: number; count: number; percentage: number }> = {}
    contributions.forEach(c => {
      if (!byMethod[c.contribution_method]) {
        byMethod[c.contribution_method] = { amount: 0, count: 0, percentage: 0 }
      }
      byMethod[c.contribution_method].amount += c.amount
      byMethod[c.contribution_method].count += 1
    })
    Object.keys(byMethod).forEach(method => {
      byMethod[method].percentage = (byMethod[method].amount / totalAmount) * 100
    })

    // By fund
    const byFund: Record<string, { amount: number; count: number; percentage: number }> = {}
    contributions.forEach(c => {
      const fundName = c.fund?.fund_name || 'General Fund'
      if (!byFund[fundName]) {
        byFund[fundName] = { amount: 0, count: 0, percentage: 0 }
      }
      byFund[fundName].amount += c.amount
      byFund[fundName].count += 1
    })
    Object.keys(byFund).forEach(fund => {
      byFund[fund].percentage = (byFund[fund].amount / totalAmount) * 100
    })

    // Trends (daily)
    const trendsByDate: Record<string, { amount: number; count: number }> = {}
    contributions.forEach(c => {
      const date = c.contribution_date
      if (!trendsByDate[date]) {
        trendsByDate[date] = { amount: 0, count: 0 }
      }
      trendsByDate[date].amount += c.amount
      trendsByDate[date].count += 1
    })
    const trends = Object.entries(trendsByDate)
      .map(([date, { amount, count }]) => ({ date, amount, count }))
      .sort((a, b) => a.date.localeCompare(b.date))

    // Top givers
    const giverTotals: Record<string, { name: string; amount: number; count: number }> = {}
    contributions.filter(c => c.member_id && c.member && c.member.profile).forEach(c => {
      if (!giverTotals[c.member_id!]) {
        giverTotals[c.member_id!] = {
          name: `${c.member.profile.first_name} ${c.member.profile.last_name}`,
          amount: 0,
          count: 0
        }
      }
      giverTotals[c.member_id!].amount += c.amount
      giverTotals[c.member_id!].count += 1
    })
    const topGivers = Object.entries(giverTotals)
      .map(([memberId, { name, amount, count }]) => ({
        member_id: memberId,
        member_name: name,
        total_amount: amount,
        transaction_count: count
      }))
      .sort((a, b) => b.total_amount - a.total_amount)
      .slice(0, 20)

    return {
      summary: {
        total_givers: uniqueGivers,
        total_amount: totalAmount,
        average_gift: totalAmount / contributions.length,
        total_transactions: contributions.length
      },
      by_type: byType,
      by_method: byMethod,
      by_fund: byFund,
      trends,
      top_givers: topGivers
    }
  }

  /**
   * Get Member Giving Statement
   */
  static async getMemberGivingStatement(
    memberId: string,
    startDate: string,
    endDate: string
  ): Promise<{
    member: any
    summary: {
      total_amount: number
      total_transactions: number
      tax_deductible_amount: number
    }
    contributions: any[]
  }> {
    const { data, error } = await supabase.rpc('get_member_giving_statement', {
      p_member_id: memberId,
      p_start_date: startDate,
      p_end_date: endDate
    })

    if (error) throw error

    // Get member details
    const { data: member } = await supabase
      .from('members')
      .select('*, profile:profiles(*)')
      .eq('id', memberId)
      .single()

    const contributions = data || []
    const totalAmount = contributions.reduce((sum: number, c: any) => sum + (c.amount || 0), 0)
    const taxDeductibleAmount = contributions
      .filter((c: any) => c.tax_deductible)
      .reduce((sum: number, c: any) => sum + (c.amount || 0), 0)

    return {
      member,
      summary: {
        total_amount: totalAmount,
        total_transactions: contributions.length,
        tax_deductible_amount: taxDeductibleAmount
      },
      contributions
    }
  }

  /**
   * Export Statement of Financial Position to PDF
   */
  static async exportBalanceSheetToPDF(
    churchId: string,
    churchName: string,
    asOfDate: string,
    data: import('@/services/financeService').StatementOfFinancialPosition
  ): Promise<Blob> {
    const doc = new jsPDF()

    // Get church data with logo
    const churchData = await getChurchData(churchId)
    const hasLogo = !!churchData.logoUrl

    // Add logo if available
    if (hasLogo) {
      await addLogoToPDF(doc, churchData.logoUrl, 14, 10, 20, 20)
    }

    // Set up header (adjusted for logo)
    const headerY = hasLogo ? 18 : 20
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    if (hasLogo) {
      doc.text(churchData.name, 40, headerY)
    } else {
      doc.text(churchName, 105, headerY, { align: 'center' })
    }

    doc.setFontSize(14)
    doc.setFont('helvetica', 'normal')
    doc.text('Statement of Financial Position', 105, headerY + 12, { align: 'center' })
    doc.text(`As of ${format(new Date(asOfDate), 'MMMM dd, yyyy')}`, 105, headerY + 20, { align: 'center' })

    doc.setFontSize(10)
    doc.setTextColor(100)
    doc.text('FASB ASC 958 - Non-Profit Format', 105, headerY + 26, { align: 'center' })
    doc.setTextColor(0)

    let yPosition = headerY + 37

    // ASSETS Section
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('ASSETS', 14, yPosition)
    yPosition += 5

    const assetRows = data.assets.map(a => [
      a.account_code,
      a.account_name,
      formatCurrencyForPDF(a.balance)
    ])

    autoTable(doc, {
      startY: yPosition,
      head: [['Code', 'Account', 'Amount']],
      body: assetRows,
      theme: 'plain',
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: 0,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 110 },
        2: { cellWidth: 40, halign: 'right' }
      },
      margin: { left: 14 }
    })

    yPosition = (doc as any).lastAutoTable.finalY + 3
    doc.setFont('helvetica', 'bold')
    doc.text('Total Assets', 14, yPosition)
    doc.text(formatCurrencyForPDF(data.total_assets), 189, yPosition, { align: 'right' })
    yPosition += 10

    // LIABILITIES Section
    doc.setFontSize(12)
    doc.text('LIABILITIES', 14, yPosition)
    yPosition += 5

    const liabilityRows = data.liabilities.map(l => [
      l.account_code,
      l.account_name,
      formatCurrencyForPDF(l.balance)
    ])

    if (liabilityRows.length > 0) {
      autoTable(doc, {
        startY: yPosition,
        head: [['Code', 'Account', 'Amount']],
        body: liabilityRows,
        theme: 'plain',
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: 0,
          fontStyle: 'bold',
          fontSize: 9
        },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 110 },
          2: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 14 }
      })
      yPosition = (doc as any).lastAutoTable.finalY + 3
    }

    doc.setFont('helvetica', 'bold')
    doc.text('Total Liabilities', 14, yPosition)
    doc.text(formatCurrencyForPDF(data.total_liabilities), 189, yPosition, { align: 'right' })
    yPosition += 10

    // NET ASSETS Section
    doc.setFontSize(12)
    doc.text('NET ASSETS', 14, yPosition)
    yPosition += 5

    const netAssetRows = data.net_assets.map(n => [
      n.account_code,
      n.account_name,
      formatCurrencyForPDF(n.balance)
    ])

    autoTable(doc, {
      startY: yPosition,
      head: [['Code', 'Account', 'Amount']],
      body: netAssetRows,
      theme: 'plain',
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: 0,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 110 },
        2: { cellWidth: 40, halign: 'right' }
      },
      margin: { left: 14 }
    })

    yPosition = (doc as any).lastAutoTable.finalY + 3
    doc.setFont('helvetica', 'bold')
    doc.text('Total Net Assets', 14, yPosition)
    doc.text(formatCurrencyForPDF(data.total_net_assets), 189, yPosition, { align: 'right' })
    yPosition += 5

    // Balance check line
    doc.setDrawColor(0)
    doc.setLineWidth(0.5)
    doc.line(14, yPosition, 195, yPosition)
    yPosition += 5

    doc.setFontSize(11)
    doc.text('Total Liabilities + Net Assets', 14, yPosition)
    doc.text(formatCurrencyForPDF(data.total_liabilities + data.total_net_assets), 189, yPosition, { align: 'right' })

    // Verification note at bottom
    const isBalanced = Math.abs(data.total_assets - (data.total_liabilities + data.total_net_assets)) < 0.01
    yPosition += 10
    doc.setFontSize(9)
    doc.setTextColor(isBalanced ? 0 : 255, isBalanced ? 150 : 0, 0)
    doc.text(
      isBalanced ? '✓ Balance Sheet is balanced' : '⚠ Balance Sheet does not balance',
      105,
      yPosition,
      { align: 'center' }
    )

    // Footer
    doc.setTextColor(100)
    doc.setFontSize(8)
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.text(
        `Generated on ${format(new Date(), 'MMMM dd, yyyy HH:mm')} | Page ${i} of ${pageCount}`,
        105,
        285,
        { align: 'center' }
      )
    }

    return doc.output('blob')
  }

  /**
   * Export Statement of Activities to PDF
   */
  static async exportStatementOfActivitiesToPDF(
    churchName: string,
    startDate: string,
    endDate: string,
    data: import('@/services/financeService').StatementOfActivities
  ): Promise<Blob> {
    const doc = new jsPDF()

    // Set up header
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text(churchName, 105, 20, { align: 'center' })

    doc.setFontSize(14)
    doc.setFont('helvetica', 'normal')
    doc.text('Statement of Activities', 105, 30, { align: 'center' })
    doc.text(
      `For the Period: ${format(new Date(startDate), 'MMM dd, yyyy')} to ${format(new Date(endDate), 'MMM dd, yyyy')}`,
      105,
      38,
      { align: 'center' }
    )

    doc.setFontSize(10)
    doc.setTextColor(100)
    doc.text('FASB ASC 958 - Non-Profit Format', 105, 44, { align: 'center' })
    doc.setTextColor(0)

    let yPosition = 55

    // REVENUE Section
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('SUPPORT AND REVENUE', 14, yPosition)
    yPosition += 5

    const revenueRows = data.revenue.map(r => [
      r.account_code,
      r.account_name,
      formatCurrencyForPDF(r.amount)
    ])

    if (revenueRows.length > 0) {
      autoTable(doc, {
        startY: yPosition,
        head: [['Code', 'Account', 'Amount']],
        body: revenueRows,
        theme: 'plain',
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: 0,
          fontStyle: 'bold',
          fontSize: 9
        },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 110 },
          2: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 14 }
      })
      yPosition = (doc as any).lastAutoTable.finalY + 3
    } else {
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100)
      doc.text('No revenue recorded for this period', 14, yPosition)
      doc.setTextColor(0)
      yPosition += 8
    }

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.text('Total Support and Revenue', 14, yPosition)
    doc.text(formatCurrencyForPDF(data.total_revenue), 189, yPosition, { align: 'right' })
    yPosition += 10

    // EXPENSES Section
    doc.setFontSize(12)
    doc.text('EXPENSES', 14, yPosition)
    yPosition += 5

    const expenseRows = data.expenses.map(e => [
      e.account_code,
      e.account_name,
      formatCurrencyForPDF(e.amount)
    ])

    if (expenseRows.length > 0) {
      autoTable(doc, {
        startY: yPosition,
        head: [['Code', 'Account', 'Amount']],
        body: expenseRows,
        theme: 'plain',
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: 0,
          fontStyle: 'bold',
          fontSize: 9
        },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 110 },
          2: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 14 }
      })
      yPosition = (doc as any).lastAutoTable.finalY + 3
    } else {
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100)
      doc.text('No expenses recorded for this period', 14, yPosition)
      doc.setTextColor(0)
      yPosition += 8
    }

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.text('Total Expenses', 14, yPosition)
    doc.text(formatCurrencyForPDF(data.total_expenses), 189, yPosition, { align: 'right' })
    yPosition += 8

    // NET INCOME/SURPLUS
    doc.setDrawColor(0)
    doc.setLineWidth(0.5)
    doc.line(14, yPosition, 195, yPosition)
    yPosition += 5

    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    const isPositive = data.net_income >= 0
    doc.text(isPositive ? 'Change in Net Assets (Surplus)' : 'Change in Net Assets (Deficit)', 14, yPosition)
    doc.text(formatCurrencyForPDF(data.net_income), 189, yPosition, { align: 'right' })

    // Add summary box
    yPosition += 15
    doc.setDrawColor(200)
    doc.setLineWidth(0.3)
    doc.roundedRect(14, yPosition, 181, 25, 2, 2)

    yPosition += 7
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text('SUMMARY', 20, yPosition)

    yPosition += 6
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.text('Total Revenue:', 20, yPosition)
    doc.text(formatCurrencyForPDF(data.total_revenue), 189, yPosition, { align: 'right' })

    yPosition += 5
    doc.text('Total Expenses:', 20, yPosition)
    doc.text(formatCurrencyForPDF(data.total_expenses), 189, yPosition, { align: 'right' })

    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Net Change:', 20, yPosition)
    doc.text(formatCurrencyForPDF(data.net_income), 189, yPosition, { align: 'right' })

    // Footer
    doc.setTextColor(100)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.text(
        `Generated on ${format(new Date(), 'MMMM dd, yyyy HH:mm')} | Page ${i} of ${pageCount}`,
        105,
        285,
        { align: 'center' }
      )
    }

    return doc.output('blob')
  }

  /**
   * Export Statement of Financial Position to Excel
   */
  static async exportBalanceSheetToExcel(
    churchName: string,
    asOfDate: string,
    data: import('@/services/financeService').StatementOfFinancialPosition
  ): Promise<Blob> {
    const workbook = XLSX.utils.book_new()

    // Create worksheet data
    const wsData: any[][] = [
      [churchName],
      ['Statement of Financial Position'],
      [`As of ${format(new Date(asOfDate), 'MMMM dd, yyyy')}`],
      ['FASB ASC 958 - Non-Profit Format'],
      [],
      ['ASSETS'],
      ['Code', 'Account Name', 'Amount'],
    ]

    // Add asset rows
    data.assets.forEach(a => {
      wsData.push([a.account_code, a.account_name, a.balance])
    })
    wsData.push(['', 'Total Assets', data.total_assets])
    wsData.push([])

    // Add liabilities
    wsData.push(['LIABILITIES'])
    wsData.push(['Code', 'Account Name', 'Amount'])
    data.liabilities.forEach(l => {
      wsData.push([l.account_code, l.account_name, l.balance])
    })
    wsData.push(['', 'Total Liabilities', data.total_liabilities])
    wsData.push([])

    // Add net assets
    wsData.push(['NET ASSETS'])
    wsData.push(['Code', 'Account Name', 'Amount'])
    data.net_assets.forEach(n => {
      wsData.push([n.account_code, n.account_name, n.balance])
    })
    wsData.push(['', 'Total Net Assets', data.total_net_assets])
    wsData.push([])
    wsData.push(['', 'Total Liabilities + Net Assets', data.total_liabilities + data.total_net_assets])
    wsData.push([])

    const isBalanced = Math.abs(data.total_assets - (data.total_liabilities + data.total_net_assets)) < 0.01
    wsData.push(['', isBalanced ? '✓ Balance Sheet is balanced' : '⚠ Balance Sheet does not balance'])

    const worksheet = XLSX.utils.aoa_to_sheet(wsData)

    // Set column widths
    worksheet['!cols'] = [
      { wch: 10 },  // Code
      { wch: 40 },  // Account Name
      { wch: 15 }   // Amount
    ]

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Sheet')

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  }

  /**
   * Export Statement of Activities to Excel
   */
  static async exportStatementOfActivitiesToExcel(
    churchName: string,
    startDate: string,
    endDate: string,
    data: import('@/services/financeService').StatementOfActivities
  ): Promise<Blob> {
    const workbook = XLSX.utils.book_new()

    // Create worksheet data
    const wsData: any[][] = [
      [churchName],
      ['Statement of Activities'],
      [`For the Period: ${format(new Date(startDate), 'MMM dd, yyyy')} to ${format(new Date(endDate), 'MMM dd, yyyy')}`],
      ['FASB ASC 958 - Non-Profit Format'],
      [],
      ['SUPPORT AND REVENUE'],
      ['Code', 'Account Name', 'Amount'],
    ]

    // Add revenue rows
    if (data.revenue.length > 0) {
      data.revenue.forEach(r => {
        wsData.push([r.account_code, r.account_name, r.amount])
      })
    } else {
      wsData.push(['', 'No revenue recorded for this period', 0])
    }
    wsData.push(['', 'Total Support and Revenue', data.total_revenue])
    wsData.push([])

    // Add expenses
    wsData.push(['EXPENSES'])
    wsData.push(['Code', 'Account Name', 'Amount'])
    if (data.expenses.length > 0) {
      data.expenses.forEach(e => {
        wsData.push([e.account_code, e.account_name, e.amount])
      })
    } else {
      wsData.push(['', 'No expenses recorded for this period', 0])
    }
    wsData.push(['', 'Total Expenses', data.total_expenses])
    wsData.push([])

    // Net income
    wsData.push(['', data.net_income >= 0 ? 'Change in Net Assets (Surplus)' : 'Change in Net Assets (Deficit)', data.net_income])
    wsData.push([])

    // Summary section
    wsData.push(['SUMMARY'])
    wsData.push(['Total Revenue:', '', data.total_revenue])
    wsData.push(['Total Expenses:', '', data.total_expenses])
    wsData.push(['Net Change:', '', data.net_income])

    const worksheet = XLSX.utils.aoa_to_sheet(wsData)

    // Set column widths
    worksheet['!cols'] = [
      { wch: 10 },  // Code
      { wch: 40 },  // Account Name
      { wch: 15 }   // Amount
    ]

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Statement of Activities')

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  }

  /**
   * Export financial report to PDF (Generic dispatcher)
   */
  static async exportReportToPDF(
    reportType: string,
    reportData: any,
    metadata?: { churchName?: string; startDate?: string; endDate?: string; asOfDate?: string }
  ): Promise<Blob> {
    const churchName = metadata?.churchName || 'Church Name'

    switch (reportType) {
      case 'balance_sheet':
      case 'statement_of_financial_position':
        return this.exportBalanceSheetToPDF(
          churchName,
          metadata?.asOfDate || new Date().toISOString(),
          reportData
        )

      case 'income_statement':
      case 'statement_of_activities':
        return this.exportStatementOfActivitiesToPDF(
          churchName,
          metadata?.startDate || new Date().toISOString(),
          metadata?.endDate || new Date().toISOString(),
          reportData
        )

      default:
        throw new Error(`PDF export not implemented for report type: ${reportType}`)
    }
  }

  /**
   * Export report to Excel (Generic dispatcher)
   */
  static async exportReportToExcel(
    reportType: string,
    reportData: any,
    metadata?: { churchName?: string; startDate?: string; endDate?: string; asOfDate?: string }
  ): Promise<Blob> {
    const churchName = metadata?.churchName || 'Church Name'

    switch (reportType) {
      case 'balance_sheet':
      case 'statement_of_financial_position':
        return this.exportBalanceSheetToExcel(
          churchName,
          metadata?.asOfDate || new Date().toISOString(),
          reportData
        )

      case 'income_statement':
      case 'statement_of_activities':
        return this.exportStatementOfActivitiesToExcel(
          churchName,
          metadata?.startDate || new Date().toISOString(),
          metadata?.endDate || new Date().toISOString(),
          reportData
        )

      default:
        throw new Error(`Excel export not implemented for report type: ${reportType}`)
    }
  }
}

export default ReportService
